import { useState, useEffect } from 'react';
import { useTenants } from '../hooks/useTenants';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';

interface SuperAdminTenant {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  plataforma?: string;
  idPlataforma?: string;
}

export function TenantSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const { tenants, currentTenant, loading, switchTenant } = useTenants();
  const { user } = useAuth();
  const { theme } = useTheme();

  // States for SUPERADMIN mode
  const [allTenants, setAllTenants] = useState<SuperAdminTenant[]>([]);
  const [selectedSuperAdminTenant, setSelectedSuperAdminTenant] = useState<string>('');
  const [loadingSuperAdmin, setLoadingSuperAdmin] = useState(false);

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Load all tenants for SUPERADMIN
  useEffect(() => {
    if (isSuperAdmin) {
      loadAllTenants();
    }
  }, [isSuperAdmin]);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadAllTenants = async () => {
    try {
      setLoadingSuperAdmin(true);
      // Buscar apenas tenants associados ao usuário (mesmo para SUPERADMIN)
      const response = await authenticatedFetch('/api/user-tenants');
      if (response.ok) {
        const data = await response.json();
        const tenantsArray = data.tenants || [];
        setAllTenants(tenantsArray);

        // Load from localStorage or select first
        const savedTenantId = localStorage.getItem('superadmin_selected_tenant');
        if (savedTenantId && tenantsArray.some((t: SuperAdminTenant) => t.id === savedTenantId)) {
          setSelectedSuperAdminTenant(savedTenantId);
        } else if (tenantsArray.length > 0) {
          setSelectedSuperAdminTenant(tenantsArray[0].id);
          localStorage.setItem('superadmin_selected_tenant', tenantsArray[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoadingSuperAdmin(false);
    }
  };

  const handleSuperAdminTenantSwitch = async (tenantId: string) => {
    try {
      setIsOpen(false);
      setLoadingSuperAdmin(true);

      const response = await authenticatedFetch('/api/user-tenants/switch', {
        method: 'POST',
        body: JSON.stringify({ tenantId })
      });

      if (!response.ok) {
        throw new Error('Erro ao trocar empresa');
      }

      const data = await response.json();
      if (data.success) {
        // Update token with new tenant context
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('superadmin_selected_tenant', tenantId);

        toast.success('Empresa alterada com sucesso');

        // Reload page to refresh all tenant-specific data
        window.location.reload();
      }
    } catch (error) {
      console.error('Erro ao trocar empresa:', error);
      toast.error('Erro ao trocar empresa');
      setLoadingSuperAdmin(false);
    }
  };

  // Non-admin users never see tenant selector (includes downgraded SuperAdmin without /superadmin URL)
  if (user?.role === 'USER') {
    return null;
  }

  // SUPERADMIN mode
  if (isSuperAdmin) {
    if (allTenants.length === 0 && !loadingSuperAdmin) {
      return null;
    }

    const selectedTenant = allTenants.find(t => t.id === selectedSuperAdminTenant);

    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
            theme === 'dark'
              ? 'border-blue-500 bg-blue-900/30 hover:bg-blue-900/50'
              : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
          }`}
          disabled={loadingSuperAdmin}
        >
          <svg className={`h-4 w-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8v-3a1 1 0 011-1h2a1 1 0 011 1v3" />
          </svg>
          <span className={`text-sm font-medium max-w-[150px] truncate ${
            theme === 'dark' ? 'text-blue-200' : 'text-blue-700'
          }`}>
            {selectedTenant?.name || 'Selecionar empresa'}
          </span>
          <svg className={`h-4 w-4 transform transition-transform ${isOpen ? 'rotate-180' : ''} ${
            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div className={`absolute right-0 mt-1 w-64 rounded-lg shadow-lg border z-20 ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            }`}>
              <div className="py-1">
                <div className={`px-3 py-2 text-xs font-medium uppercase tracking-wider border-b ${
                  theme === 'dark'
                    ? 'text-blue-400 border-gray-700'
                    : 'text-blue-600 border-gray-100'
                }`}>
                  🔧 Modo Super Admin
                </div>
                {allTenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => handleSuperAdminTenantSwitch(tenant.id)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      tenant.id === selectedSuperAdminTenant
                        ? theme === 'dark'
                          ? 'bg-blue-900/50 text-blue-200'
                          : 'bg-blue-50 text-blue-700'
                        : theme === 'dark'
                          ? 'text-gray-200 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{tenant.name}</div>
                        <div className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>{tenant.slug}</div>
                      </div>
                      {tenant.id === selectedSuperAdminTenant && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Regular user mode
  if (!tenants || tenants.length <= 1) {
    return null;
  }

  const handleTenantSwitch = async (tenantId: string) => {
    setIsOpen(false);
    await switchTenant(tenantId);
  };

  return (
    <div className="relative">
      {/* Tenant Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
          theme === 'dark'
            ? 'border-gray-600 hover:bg-gray-700'
            : 'border-gray-200 hover:bg-gray-50'
        }`}
        disabled={loading}
      >
        {/* Building icon */}
        <svg className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8v-3a1 1 0 011-1h2a1 1 0 011 1v3" />
        </svg>
        <span className={`text-sm max-w-[150px] truncate ${
          theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
          {currentTenant?.name || 'Selecionar empresa'}
        </span>
        {/* Chevron down icon */}
        <svg className={`h-4 w-4 transform transition-transform ${isOpen ? 'rotate-180' : ''} ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className={`absolute right-0 mt-1 w-64 rounded-lg shadow-lg border z-20 ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}>
            <div className="py-1">
              <div className={`px-3 py-2 text-xs font-medium uppercase tracking-wider border-b ${
                theme === 'dark'
                  ? 'text-gray-400 border-gray-700'
                  : 'text-gray-500 border-gray-100'
              }`}>
                Empresas Disponíveis
              </div>

              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSwitch(tenant.id)}
                  disabled={loading || tenant.current}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
                    tenant.current
                      ? theme === 'dark'
                        ? 'bg-blue-900/50 text-blue-200'
                        : 'bg-blue-50 text-blue-700'
                      : theme === 'dark'
                        ? 'text-gray-200 hover:bg-gray-700'
                        : 'text-gray-700 hover:bg-gray-50'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div>
                    <div className="font-medium">{tenant.name}</div>
                    <div className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {tenant.role === 'SUPERADMIN' ? 'Super Admin' : tenant.role}
                    </div>
                  </div>

                  {tenant.current && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}